import { useRef, useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/SimpleToast";
import { mailingApi } from "@/lib/api";
import {
  Type, Heading, Image as ImageIcon, MousePointerClick, Minus, MoveVertical,
  ArrowUp, ArrowDown, Trash2, AlignLeft, AlignCenter, AlignRight, Loader2, Upload,
  Sparkles, SpellCheck, Wand2, ImagePlus,
} from "lucide-react";
import { createBlock, PLACEHOLDERS, BLOCK_LABELS } from "./emailBlocks";
import { AiTextDialog } from "./AiTextDialog";
import { AiImageDialog } from "./AiImageDialog";

const TONE_OPTIONS = [
  { value: "freundlich", label: "Freundlich" },
  { value: "formell", label: "Formell" },
  { value: "ueberzeugend", label: "Überzeugend" },
  { value: "kurz", label: "Kürzer" },
];

const ADD_BUTTONS = [
  { type: "text", icon: Type },
  { type: "heading", icon: Heading },
  { type: "image", icon: ImageIcon },
  { type: "button", icon: MousePointerClick },
  { type: "divider", icon: Minus },
  { type: "spacer", icon: MoveVertical },
];

const AlignControl = ({ value, onChange, testid }) => (
  <div className="inline-flex rounded-md border border-border overflow-hidden">
    {[
      { v: "left", Icon: AlignLeft },
      { v: "center", Icon: AlignCenter },
      { v: "right", Icon: AlignRight },
    ].map(({ v, Icon }) => (
      <button
        key={v}
        type="button"
        onClick={() => onChange(v)}
        className={`p-1.5 transition-colors ${value === v ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
        data-testid={`${testid}-align-${v}`}
      >
        <Icon className="w-3.5 h-3.5" />
      </button>
    ))}
  </div>
);

export function EmailBlockEditor({ blocks, onChange }) {
  const toast = useToast();
  const lastFocused = useRef(null); // { id, el }
  const uploadingRef = useRef({});
  const fileInputs = useRef({});
  const [aiTextOpen, setAiTextOpen] = useState(false);
  const [aiImageBlockId, setAiImageBlockId] = useState(null);
  const [busyBlockId, setBusyBlockId] = useState(null);

  const list = Array.isArray(blocks) ? blocks : [];

  const update = useCallback((id, patch) => {
    onChange(list.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  }, [list, onChange]);

  const add = (type) => onChange([...list, createBlock(type)]);

  const remove = (id) => onChange(list.filter((b) => b.id !== id));

  const insertAiText = (text) => {
    onChange([...list, { ...createBlock("text"), content: text }]);
    toast.success("KI-Textblock eingefügt");
  };

  const runAiOnBlock = async (block, action, tone) => {
    if (!(block.content || "").trim()) { toast.error("Der Block enthält keinen Text"); return; }
    setBusyBlockId(block.id);
    try {
      const res = await mailingApi.aiText({ action, tone, text: block.content });
      update(block.id, { content: res.data.text || block.content });
      toast.success(action === "spellcheck" ? "Rechtschreibung korrigiert" : "Tonfall angepasst");
    } catch (e) {
      toast.error(e.response?.data?.detail || "KI-Bearbeitung fehlgeschlagen");
    } finally {
      setBusyBlockId(null);
    }
  };

  const move = (id, dir) => {
    const idx = list.findIndex((b) => b.id === id);
    const to = idx + dir;
    if (idx < 0 || to < 0 || to >= list.length) return;
    const next = [...list];
    [next[idx], next[to]] = [next[to], next[idx]];
    onChange(next);
  };

  const insertPlaceholder = (token) => {
    const focused = lastFocused.current;
    if (!focused) {
      toast.error("Bitte zuerst in ein Text- oder Überschrift-Feld klicken");
      return;
    }
    const { id, el } = focused;
    const block = list.find((b) => b.id === id);
    if (!block) return;
    const start = el?.selectionStart ?? (block.content || "").length;
    const end = el?.selectionEnd ?? start;
    const val = block.content || "";
    const next = `${val.slice(0, start)}${token}${val.slice(end)}`;
    update(id, { content: next });
    requestAnimationFrame(() => {
      if (el) {
        const pos = start + token.length;
        el.focus();
        try { el.setSelectionRange(pos, pos); } catch { /* noop */ }
      }
    });
  };

  const handleUpload = async (id, file) => {
    if (!file) return;
    uploadingRef.current[id] = true;
    onChange(list.map((b) => (b.id === id ? { ...b, _uploading: true } : b)));
    try {
      const res = await mailingApi.uploadAsset(file);
      onChange(list.map((b) => (b.id === id ? { ...b, src: res.data.path, _uploading: false } : b)));
      toast.success("Bild hochgeladen");
    } catch (error) {
      onChange(list.map((b) => (b.id === id ? { ...b, _uploading: false } : b)));
      toast.error(error.response?.data?.detail || "Upload fehlgeschlagen");
    }
  };

  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const imgPreviewSrc = (src) => (src?.startsWith("/") ? `${backendUrl}${src}` : src);

  return (
    <div className="space-y-3" data-testid="email-block-editor">
      {/* Add-Toolbar */}
      <div className="flex flex-wrap gap-1.5">
        {ADD_BUTTONS.map(({ type, icon: Icon }) => (
          <Button
            key={type}
            type="button"
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => add(type)}
            data-testid={`add-block-${type}`}
          >
            <Icon className="w-3.5 h-3.5 mr-1.5" /> {BLOCK_LABELS[type]}
          </Button>
        ))}
        <Button
          type="button"
          size="sm"
          className="h-8 ml-auto bg-primary/90 hover:bg-primary"
          onClick={() => setAiTextOpen(true)}
          data-testid="ai-text-open"
        >
          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> KI-Text
        </Button>
      </div>

      {/* Platzhalter-Chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-muted-foreground mr-1">Platzhalter:</span>
        {PLACEHOLDERS.map((p) => (
          <button
            key={p.token}
            type="button"
            onClick={() => insertPlaceholder(p.token)}
            className="text-xs px-2 py-1 rounded-full border border-border bg-muted/40 hover:bg-primary/10 hover:border-primary/40 transition-colors"
            data-testid={`placeholder-${p.label.toLowerCase()}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Blöcke */}
      <div className="space-y-2.5">
        {list.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6 border border-dashed rounded-lg">
            Noch keine Inhalte – oben einen Block hinzufügen.
          </p>
        )}
        {list.map((b, idx) => (
          <div key={b.id} className="rounded-lg border border-border bg-card p-3" data-testid={`block-${b.type}-${idx}`}>
            <div className="flex items-center justify-between mb-2">
              <Badge variant="outline" className="text-[10px]">{BLOCK_LABELS[b.type]}</Badge>
              <div className="flex items-center gap-0.5">
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={idx === 0} onClick={() => move(b.id, -1)} data-testid={`block-up-${idx}`}>
                  <ArrowUp className="w-3.5 h-3.5" />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={idx === list.length - 1} onClick={() => move(b.id, 1)} data-testid={`block-down-${idx}`}>
                  <ArrowDown className="w-3.5 h-3.5" />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => remove(b.id)} data-testid={`block-delete-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            {(b.type === "text" || b.type === "heading") && (
              <div className="space-y-2">
                <Textarea
                  value={b.content || ""}
                  rows={b.type === "heading" ? 1 : 4}
                  placeholder={b.type === "heading" ? "Überschrift…" : "Text… (Platzhalter oben einfügen)"}
                  onChange={(e) => update(b.id, { content: e.target.value })}
                  onFocus={(e) => { lastFocused.current = { id: b.id, el: e.target }; }}
                  data-testid={`block-content-${idx}`}
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <AlignControl value={b.align} onChange={(v) => update(b.id, { align: v })} testid={`block-${idx}`} />
                  <div className="flex items-center gap-1 ml-auto">
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" disabled={busyBlockId === b.id} onClick={() => runAiOnBlock(b, "spellcheck")} data-testid={`block-ai-spellcheck-${idx}`}>
                      {busyBlockId === b.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <SpellCheck className="w-3.5 h-3.5 mr-1" />}
                      Rechtschreibung
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" disabled={busyBlockId === b.id} data-testid={`block-ai-tone-${idx}`}>
                          <Wand2 className="w-3.5 h-3.5 mr-1" /> Tonfall
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {TONE_OPTIONS.map((t) => (
                          <DropdownMenuItem key={t.value} onClick={() => runAiOnBlock(b, "tone", t.value)} data-testid={`block-ai-tone-${t.value}-${idx}`}>
                            {t.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            )}

            {b.type === "image" && (
              <div className="space-y-2">
                <input
                  ref={(el) => (fileInputs.current[b.id] = el)}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleUpload(b.id, f); }}
                  data-testid={`block-image-input-${idx}`}
                />
                {b.src ? (
                  <img src={imgPreviewSrc(b.src)} alt={b.alt || ""} className="max-h-32 rounded border" data-testid={`block-image-preview-${idx}`} />
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" className="h-8" disabled={b._uploading} onClick={() => fileInputs.current[b.id]?.click()} data-testid={`block-image-upload-${idx}`}>
                    {b._uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                    {b.src ? "Bild ersetzen" : "Bild hochladen"}
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => setAiImageBlockId(b.id)} data-testid={`block-image-ai-${idx}`}>
                    <ImagePlus className="w-3.5 h-3.5 mr-1.5" /> KI-Bild erzeugen
                  </Button>
                  <AlignControl value={b.align} onChange={(v) => update(b.id, { align: v })} testid={`block-${idx}`} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Breite (px)</Label>
                    <Input type="number" min={60} max={528} value={b.width || 528} onChange={(e) => update(b.id, { width: parseInt(e.target.value || "528", 10) })} className="h-8" data-testid={`block-image-width-${idx}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Link (optional)</Label>
                    <Input value={b.href || ""} placeholder="https://…" onChange={(e) => update(b.id, { href: e.target.value })} className="h-8" data-testid={`block-image-href-${idx}`} />
                  </div>
                </div>
                <Input value={b.alt || ""} placeholder="Alternativtext (Barrierefreiheit)" onChange={(e) => update(b.id, { alt: e.target.value })} className="h-8" data-testid={`block-image-alt-${idx}`} />
              </div>
            )}

            {b.type === "button" && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Beschriftung</Label>
                    <Input value={b.label || ""} onChange={(e) => update(b.id, { label: e.target.value })} className="h-8" data-testid={`block-button-label-${idx}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Ziel-URL</Label>
                    <Input value={b.url || ""} placeholder="https://…" onChange={(e) => update(b.id, { url: e.target.value })} className="h-8" data-testid={`block-button-url-${idx}`} />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="inline-flex rounded-md border border-border overflow-hidden">
                    {[
                      { c: "orange", bg: "#F39200" },
                      { c: "blue", bg: "#0061B0" },
                    ].map(({ c, bg }) => (
                      <button key={c} type="button" onClick={() => update(b.id, { color: c })}
                        className={`px-3 py-1.5 text-xs font-medium text-white transition-opacity ${b.color === c ? "opacity-100 ring-2 ring-offset-1 ring-primary" : "opacity-70"}`}
                        style={{ background: bg }} data-testid={`block-button-color-${c}-${idx}`}>
                        {c === "orange" ? "Orange" : "Blau"}
                      </button>
                    ))}
                  </div>
                  <AlignControl value={b.align} onChange={(v) => update(b.id, { align: v })} testid={`block-${idx}`} />
                </div>
              </div>
            )}

            {b.type === "spacer" && (
              <div className="space-y-1">
                <Label className="text-xs">Höhe (px)</Label>
                <Input type="number" min={4} max={120} value={b.height || 20} onChange={(e) => update(b.id, { height: parseInt(e.target.value || "20", 10) })} className="h-8 w-28" data-testid={`block-spacer-height-${idx}`} />
              </div>
            )}

            {b.type === "divider" && (
              <div className="border-t border-dashed my-1" />
            )}
          </div>
        ))}
      </div>

      <AiTextDialog open={aiTextOpen} onOpenChange={setAiTextOpen} onInsert={insertAiText} />
      <AiImageDialog
        open={!!aiImageBlockId}
        onOpenChange={(o) => { if (!o) setAiImageBlockId(null); }}
        onGenerated={(path) => { if (aiImageBlockId) update(aiImageBlockId, { src: path }); setAiImageBlockId(null); }}
      />
    </div>
  );
}
